export type VoteChoice = "approve" | "reject" | "cool";
export type RequestStatus = "open" | "closed" | "withdrawn";
export type ExecutionOutcome = "obey" | "defy" | "cooling";

export interface BureauComment {
  id: string;
  nickname: string;
  choice: VoteChoice;
  presetCode: string | null;
  presetLabel: string;
  body: string;
  visible: boolean;
  createdAt: number;
}

export interface BureauData {
  request: {
    slug: string;
    creatorNickname: string;
    productName: string;
    priceCents: number;
    reason: string;
    imageAvailable: boolean;
    imageUrl: string | null;
    imageExpiresAt: number | null;
    closingMode: "vote_target" | "deadline";
    voteTarget: 3 | 5 | 8 | null;
    closesAt: number | null;
    status: RequestStatus;
    verdict: VoteChoice | null;
    closedEarly: boolean;
    closedAt: number | null;
    executionOutcome: ExecutionOutcome | null;
    createdAt: number;
    expiresAt: number;
  };
  viewer: {
    nickname: string;
    hasVoted: boolean;
    voteChoice: VoteChoice | null;
    canManage: boolean;
    isCreatorDevice: boolean;
    canSeeResults: boolean;
  };
  counts: { approve: number; reject: number; cool: number; total: number } | null;
  comments: BureauComment[];
}

export interface AdminRequestItem {
  slug: string;
  creatorNickname: string;
  productName: string;
  priceCents: number;
  status: RequestStatus;
  verdict: VoteChoice | null;
  createdAt: number;
  closedAt: number | null;
  expiresAt: number;
  imageStored: boolean;
  imageAvailable: boolean;
  imageExpiresAt: number | null;
  voteCount: number;
  commentCount: number;
}

export interface AdminRequestPage {
  summary: { total: number; open: number; closed: number; withdrawn: number; images: number };
  requests: AdminRequestItem[];
  nextBefore: number | null;
}

export interface AdminDeleteResult {
  deleted: number;
  skippedOpen: number;
  notFound: number;
  votesDeleted: number;
  commentsDeleted: number;
  imagesDeleted: number;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
  }
}

async function requestJson<T>(path: string, init: RequestInit = {}, manageToken?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (manageToken) headers.set("X-Manage-Token", manageToken);
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const payload = (await response.json().catch(() => ({ error: "服务响应无法解析", code: "invalid_response" }))) as {
    error?: string;
    code?: string;
  } & T;
  if (!response.ok) {
    throw new ApiClientError(payload.error ?? "操作失败，请稍后再试", response.status, payload.code ?? "request_failed");
  }
  return payload;
}

async function adminRequestJson<T>(path: string, adminKey: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Admin-Key", adminKey);
  return requestJson<T>(path, { ...init, headers });
}

export async function startSession(): Promise<{ nickname: string }> {
  return requestJson("/api/session");
}

export async function createRequest(formData: FormData): Promise<{
  slug: string;
  publicUrl: string;
  manageUrl: string;
  manageToken: string;
}> {
  return requestJson("/api/requests", { method: "POST", body: formData });
}

export async function getRequest(slug: string, manageToken?: string): Promise<BureauData> {
  return requestJson(`/api/requests/${encodeURIComponent(slug)}`, {}, manageToken);
}

export async function submitVote(
  slug: string,
  body: { nickname: string; choice: VoteChoice; presetCode: string; comment: string },
): Promise<BureauData> {
  return requestJson(`/api/requests/${encodeURIComponent(slug)}/vote`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function closeRequest(slug: string, token: string): Promise<BureauData> {
  return requestJson(
    `/api/requests/${encodeURIComponent(slug)}/close`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
    token,
  );
}

export async function setOutcome(
  slug: string,
  token: string,
  outcome: ExecutionOutcome,
): Promise<BureauData> {
  return requestJson(
    `/api/requests/${encodeURIComponent(slug)}/outcome`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outcome }),
    },
    token,
  );
}

export async function setCommentVisibility(
  slug: string,
  commentId: string,
  token: string,
  visible: boolean,
): Promise<BureauData> {
  return requestJson(
    `/api/requests/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visible }),
    },
    token,
  );
}

export async function deleteRequest(slug: string, token: string): Promise<void> {
  await requestJson(`/api/requests/${encodeURIComponent(slug)}`, { method: "DELETE" }, token);
}

export async function getAdminRequests(
  adminKey: string,
  filters: { status: RequestStatus | "all"; olderThanDays: 0 | 30 | 60 | 90; before?: number },
): Promise<AdminRequestPage> {
  const search = new URLSearchParams({ status: filters.status, olderThanDays: String(filters.olderThanDays) });
  if (filters.before) search.set("before", String(filters.before));
  return adminRequestJson(`/api/admin/requests?${search}`, adminKey);
}

export async function adminDeleteRequest(slug: string, adminKey: string): Promise<AdminDeleteResult> {
  return adminRequestJson(`/api/admin/requests/${encodeURIComponent(slug)}`, adminKey, { method: "DELETE" });
}

export async function adminBulkDelete(slugs: string[], adminKey: string): Promise<AdminDeleteResult> {
  return adminRequestJson("/api/admin/cleanup", adminKey, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slugs }),
  });
}
